import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import CategoryPicker from './components/CategoryPicker'
import ImagePicker from './components/ImagePicker'
import { fetchCategories, fetchDepartments, submitReport } from './api/issuesApi'

// mode: 'staff' | 'student' — picks the right auth/transport in issuesApi
export default function IssueSubmit({ mode = 'staff' }) {
  const navigate = useNavigate()
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])
  const [loadingCats, setLoadingCats] = useState(true)
  const [form, setForm] = useState({ cat_id: 0, dept_id: 0, title: '', description: '', location: '' })
  const [images, setImages] = useState([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    Promise.all([fetchCategories(mode), fetchDepartments(mode)])
      .then(([cats, depts]) => { setCategories(cats); setDepartments(depts) })
      .catch(err => Swal.fire('ผิดพลาด', err.message || 'โหลดข้อมูลไม่สำเร็จ', 'error'))
      .finally(() => setLoadingCats(false))
  }, [mode])

  const selectedCat = categories.find(c => c.cat_id === form.cat_id)
  const needDeptPick = !!selectedCat?.cat_allow_dept_select

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.cat_id) {
      Swal.fire('กรุณาเลือกประเภทปัญหา', '', 'warning'); return
    }
    if (needDeptPick && !form.dept_id) {
      Swal.fire('กรุณาเลือกฝ่ายที่รับผิดชอบ', '', 'warning'); return
    }
    if (!form.title.trim() || !form.description.trim()) {
      Swal.fire('กรุณากรอกหัวข้อและรายละเอียด', '', 'warning'); return
    }
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('cat_id', String(form.cat_id))
      if (needDeptPick) fd.append('dept_id', String(form.dept_id))
      fd.append('ir_title', form.title.trim())
      fd.append('ir_description', form.description.trim())
      if (form.location.trim()) fd.append('ir_location', form.location.trim())
      images.forEach(img => fd.append('images', img))

      const { ir_id, ir_code, dept_id: _dept_id } = await submitReport(mode, fd)
      await Swal.fire({
        icon: 'success',
        title: 'ส่งรายงานเรียบร้อย',
        text: `รหัสรายงาน: ${ir_code}`,
        confirmButtonText: 'ดูรายละเอียด',
      })
      navigate(`/issues/detail/${ir_id}`)
    } catch (err) {
      Swal.fire('ส่งไม่สำเร็จ', err.response?.data?.error || err.message, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={mode === 'staff' ? 'main main-app p-3 p-lg-4' : ''} style={mode === 'staff' ? { maxWidth: 720 } : {}}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="ri-tools-fill fs-3 text-primary" />
        <h4 className="mb-0">แจ้งปัญหา / แจ้งซ่อม</h4>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-3">
          <div className="card-body">
            <label className="form-label fw-semibold mb-2">
              ประเภทปัญหา <span className="text-danger">*</span>
            </label>
            {loadingCats
              ? <div className="text-center py-3"><span className="spinner-border" /></div>
              : <CategoryPicker
                  categories={categories}
                  selected={form.cat_id}
                  onSelect={(id) => setForm(f => ({ ...f, cat_id: id, dept_id: 0 }))}
                />}
            {needDeptPick ? (
              <div className="mt-3">
                <label className="form-label mb-1">
                  ส่งเรื่องไปยังฝ่าย <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={form.dept_id || ''}
                  onChange={e => setForm(f => ({ ...f, dept_id: Number(e.target.value) }))}
                >
                  <option value="">-- เลือกฝ่ายที่รับผิดชอบ --</option>
                  {departments.map(d => (
                    <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>
                  ))}
                </select>
              </div>
            ) : selectedCat && (
              <div className="alert alert-info mt-2 mb-0 py-2 small">
                <i className="ri-route-line me-1" />
                รายงานนี้จะถูกส่งไปยัง <strong>{selectedCat.default_dept_name}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">หัวข้อ <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น หลอดไฟห้อง 305 ดับ"
                value={form.title}
                maxLength={200}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">รายละเอียด <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="อธิบายปัญหาให้ชัดเจน..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="mb-0">
              <label className="form-label">สถานที่ / จุดสังเกต</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น อาคาร 3 ชั้น 2 ห้อง 305"
                value={form.location}
                maxLength={200}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <label className="form-label fw-semibold mb-2">รูปภาพประกอบ</label>
            <ImagePicker images={images} onChange={setImages} disabled={submitting} />
          </div>
        </div>

        <div
          className="position-sticky bottom-0 bg-body pt-2 pb-3"
          style={{ zIndex: 5 }}
        >
          <button
            type="submit"
            className="btn btn-primary btn-lg w-100"
            disabled={submitting}
          >
            {submitting
              ? <><span className="spinner-border spinner-border-sm me-2" />กำลังส่ง...</>
              : <><i className="ri-send-plane-fill me-2" />ส่งรายงาน</>}
          </button>
        </div>
      </form>
    </div>
  )
}
